<app-generic-popup
  [(visible)]="visible"
  i18n-title="@@fillTemplate"
  title="Fill in template"
  [titleIcon]="step === 'issues' ? 'alert' : undefined"
  width="min(600px, 92vw)"
  maxHeight="80vh"
  [buttons]="buttons"
  [leftButtons]="leftButtons"
  [allowOutsideClick]="false"
  (onShowing)="onShowing()"
  (onHiding)="onHiding()"
>
  <!-- Step 1: template selection or own upload (#1209) -->
  <div *ngIf="step === 'select'" class="ftm-step">
    <div class="ftm-label" *ngIf="showTemplateDropdown || showFixedTemplate" i18n="@@selectTemplate">Select template</div>
    <dx-drop-down-box
      *ngIf="showTemplateDropdown"
      class="ftm-template-select"
      [(opened)]="templateDropDownOpened"
      [value]="selectedTemplateName"
      [showClearButton]="true"
      [acceptCustomValue]="true"
      i18n-placeholder="@@selectTemplatePlaceholder"
      placeholder="Select a template..."
      (onValueChanged)="onTemplateFieldValueChanged($event)"
    >
      <div *dxTemplate="let data of 'content'">
        <dx-tree-view
          class="ftm-template-tree"
          [items]="templateTree"
          dataStructure="plain"
          keyExpr="id"
          parentIdExpr="parentId"
          displayExpr="name"
          itemTemplate="templateTreeItem"
          [searchEnabled]="true"
          searchMode="contains"
          selectionMode="single"
          expandEvent="click"
          (onItemClick)="onTemplateTreeItemClick($event)"
        >
          <div *dxTemplate="let data of 'templateTreeItem'" class="ftm-template-tree-item">
            <app-svg-icon [icon]="data.icon === 'folder' ? 'folder' : 'document'" [size]="14"></app-svg-icon>
            <span>{{ data.name }}</span>
          </div>
        </dx-tree-view>
      </div>
    </dx-drop-down-box>

    <!-- A task usually has exactly one template configured — nothing to actually choose, so show
         it as a fixed value instead of a dropdown. A task with several (e.g. a whole folder
         assigned) still gets the real picker above. Stays visible either way (like the own-file
         drop zone below it) and just toggles selected/deselected, rather than vanishing on clear
         with no way back short of reopening the whole dialog. -->
    <div
      *ngIf="showFixedTemplate"
      class="ftm-template-fixed"
      [class.ftm-template-fixed-inactive]="!fixedTemplateSelected"
      [class.ftm-template-fixed-clickable]="!fixedTemplateSelected"
      (click)="!fixedTemplateSelected && selectFixedTemplate()"
    >
      <app-svg-icon
        icon="document"
        [size]="16"
        [color]="fixedTemplateSelected ? 'var(--color-primary)' : 'var(--color-grey-400, #b0b0b0)'"
      ></app-svg-icon>
      <span class="ftm-template-fixed-name">{{ templates[0].name }}</span>
      <app-svg-icon
        *ngIf="fixedTemplateSelected"
        icon="cancel"
        [size]="16"
        class="ftm-template-fixed-remove"
        role="button"
        tabindex="0"
        i18n-title="@@remove"
        title="Remove"
        (click)="$event.stopPropagation(); clearTemplateSelection()"
        (keydown.enter)="$event.stopPropagation(); clearTemplateSelection()"
      ></app-svg-icon>
      <span *ngIf="!fixedTemplateSelected" class="ftm-template-fixed-select" i18n="@@select">Select</span>
    </div>

    <div class="ftm-label ftm-or" i18n="@@orUploadOwnTemplate">or upload your own template</div>
    <div
      class="ftm-drop-zone"
      [class.drag-over]="isDragOver"
      (drop)="onDrop($event)"
      (dragover)="onDragOver($event)"
      (dragleave)="onDragLeave()"
      (click)="ownFileInput.click()"
    >
      <app-svg-icon icon="cloud-upload" [size]="30" color="var(--color-primary)"></app-svg-icon>
      <div *ngIf="ownFile == null" class="ftm-drop-text">
        <span i18n="@@dragAndDrop">Drag &amp; drop</span>
        <span i18n="@@orClickToSelectFile">or click to select a file</span>
      </div>
      <div *ngIf="ownFile != null" class="ftm-drop-text ftm-own-file">{{ ownFile.name }}</div>
      <input #ownFileInput type="file" [accept]="acceptedExtensions" style="display: none" (change)="onOwnFileSelected($event.target)" />
    </div>
  </div>

  <!-- Step 2: multi-select — linked projects of a contract (#1230), or land owners for a Serienbrief -->
  <div *ngIf="step === 'projects'" class="ftm-step">
    <div class="ftm-label" *ngIf="context === 'propertyOwner'; else contractProjectsHint" i18n="@@selectPropertyOwnersHint">
      Please select the land owners the template should be filled for. One document is created per owner.
    </div>
    <ng-template #contractProjectsHint>
      <div class="ftm-label" i18n="@@contractLinkedToMultipleProjects">
        This contract is linked to several projects. Please select at least one project the template should be filled for.
      </div>
    </ng-template>
    <label class="ftm-project-option ftm-select-all">
      <input type="checkbox" [checked]="allSelected" (change)="toggleAll()" />
      <span *ngIf="context === 'propertyOwner'; else selectAllProjectsLabel" i18n="@@selectAllOwners">Select all land owners</span>
      <ng-template #selectAllProjectsLabel><span i18n="@@selectAllProjects">Select all projects</span></ng-template>
    </label>
    <label class="ftm-project-option" *ngFor="let option of selectionOptions; trackBy: trackById">
      <input
        type="checkbox"
        [checked]="selectedIds.includes(option.id)"
        (change)="toggleSelection(option.id)"
      />
      <span>{{ option.name }}</span>
    </label>
  </div>

  <!-- Step 3: validation / fill progress (#1210) -->
  <div *ngIf="step === 'validating'" class="ftm-step ftm-validating">
    <div class="ftm-label" i18n="@@documentIsBeingChecked">The document is being checked and filled...</div>
    <dx-progress-bar
      [min]="0"
      [max]="100"
      [value]="validationProgress"
      [statusFormat]="progressStatusFormat"
      width="100%"
    ></dx-progress-bar>
  </div>

  <!-- Step 3 results: the issue cases (#1210 cases 2–4) -->
  <div *ngIf="step === 'issues'" class="ftm-step">
    <ng-container *ngIf="validation.noFillableFields; else fieldIssues">
      <div class="ftm-issue-title" i18n="@@noFillableFieldsFound">No fillable fields found</div>
      <div class="ftm-label" i18n="@@noFillableFieldsInfo">
        The system does not recognize any fillable fields in this document. Automatic completion is not possible.
      </div>
    </ng-container>
    <ng-template #fieldIssues>
      <div class="ftm-label" i18n="@@continueDespiteIssuesQuestion">
        These fields will remain empty. Do you want to continue with the document?
      </div>
      <div *ngIf="validation.missingDataFields.length > 0" class="ftm-issue-block">
        <div class="ftm-issue-title" i18n="@@missingDataFields">Fields without stored data</div>
        <div class="ftm-issue-list">
          <span class="ftm-field-tag" *ngFor="let field of validation.missingDataFields">{{ '{{' + field + '}}' }}</span>
        </div>
      </div>
      <div *ngIf="validation.unsupportedFields.length > 0" class="ftm-issue-block">
        <div class="ftm-issue-title" i18n="@@unsupportedFields">Unknown fields</div>
        <div class="ftm-issue-list">
          <span class="ftm-field-tag" *ngFor="let field of validation.unsupportedFields">{{ '{{' + field + '}}' }}</span>
        </div>
      </div>
    </ng-template>
  </div>

  <!-- Serienbrief results: the run stays open so a partial failure remains visible and the user
       keeps the ZIP and per-letter preview after filing. Every other context still closes the
       modal and opens one preview tab per document (see fill-template-preview.page.ts). -->
  <div *ngIf="step === 'results'" class="ftm-step ftm-results">
    <div class="ftm-results-banner">
      <app-svg-icon icon="checkmark-circle" [size]="18" color="var(--color-primary, #629d37)"></app-svg-icon>
      <span i18n="@@serienbriefCreated">The documents have been created.</span>
    </div>
    <div class="ftm-results-hint" i18n="@@serienbriefResultsHint">
      Add the documents to the property owners or download them. Documents that are not added are
      not saved when you close this window.
    </div>

    <div *ngIf="failedCount > 0 && !addingAll" class="ftm-results-error">
      <app-svg-icon icon="alert" [size]="16" color="var(--color-text-error, #c20909)"></app-svg-icon>
      <span i18n="@@someDocumentsFailed">
        {{ failedCount }} of {{ generatedDocs.length }} documents could not be added. Retry the
        failed rows or download them.
      </span>
    </div>

    <!-- Batch filing: choose the type once, file the whole run. Only shown when the host
         provides a filing callback (otherwise the run is download-only). -->
    <div *ngIf="addToContext != null" class="ftm-batch-bar">
      <dx-select-box
        class="gpd-input ftm-doc-type"
        [items]="documentTypes"
        valueExpr="id"
        displayExpr="name"
        [(value)]="selectedDocumentTypeId"
        (onValueChanged)="onDocumentTypeChanged()"
        i18n-placeholder="@@selectDocumentType"
        placeholder="Select document type..."
      ></dx-select-box>
      <app-gpd-button
        *ngIf="generatedDocs.length > 1"
        [text]="addAllLabel"
        icon="add"
        iconColor="white"
        [hint]="addBlockedHint"
        [disabled]="!canAddAll"
        (onClick)="addAll()"
      ></app-gpd-button>
    </div>

    <div class="ftm-results-count">
      <span i18n="@@documentsSectionLabel">Documents</span>
      <span class="ftm-results-count-num">{{ generatedDocs.length }}</span>
    </div>

    <div class="ftm-results-list">
      <div class="ftm-result-row" *ngFor="let entry of generatedDocs">
        <app-svg-icon
          class="ftm-result-icon"
          icon="document"
          [size]="18"
          color="var(--color-grey-500, #8a8a8a)"
        ></app-svg-icon>
        <div class="ftm-result-name">
          <div class="ftm-result-owner">{{ entry.doc.targetName }}</div>
          <div class="ftm-result-file">{{ entry.doc.name }}{{ entry.doc.extension }}</div>
        </div>
        <div class="ftm-result-actions">
          <app-gpd-button
            type="text"
            i18n-text="@@preview"
            text="Preview"
            icon="document"
            i18n-hint="@@opensInNewTab"
            hint="Opens in a new tab"
            [disabled]="entry.previewLoading === true"
            (onClick)="previewDoc(entry)"
          ></app-gpd-button>
          <span *ngIf="entry.doc.addFailed && !entry.doc.added" class="ftm-result-failed">
            <app-svg-icon icon="alert" [size]="14" color="var(--color-text-error, #c20909)"></app-svg-icon>
            <span i18n="@@addFailedShort">Failed</span>
          </span>
          <app-gpd-button
            *ngIf="addToContext != null && !entry.doc.added && !entry.doc.adding"
            type="text"
            [text]="entry.doc.addFailed ? retryLabel : addLabel"
            [icon]="entry.doc.addFailed ? 'refresh' : 'add'"
            [hint]="needsDocumentType ? addBlockedHint : addToContextLabel"
            [disabled]="addingAll || selectedDocumentTypeId == null"
            (onClick)="addOne(entry.doc)"
          ></app-gpd-button>
          <span *ngIf="entry.doc.adding" class="ftm-result-adding">
            <dx-load-indicator [height]="14" [width]="14"></dx-load-indicator>
            <span i18n="@@adding">Adding…</span>
          </span>
          <span *ngIf="entry.doc.added" class="ftm-result-added">
            <app-svg-icon icon="check" [size]="14" color="var(--color-primary, #629d37)"></app-svg-icon>
            <span i18n="@@added">Added</span>
          </span>
        </div>
      </div>
    </div>
  </div>
</app-generic-popup>

<!-- #1231: feedback for "add to task/contract", triggered from the preview tab after this modal
     has already closed — rendered here (outside the popup) so it still shows on the host page. -->
<app-validation-toast #successToast></app-validation-toast>
<app-validation-toast #errorToast></app-validation-toast>
